/**
*
*   Content Block
*       - A left/right content block + image or video
*
**/
'use client';

import Image from '@/components/image';
import Button from '@/components/button';
import { header } from '@/components/header/wrapper';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';

//Load Module
gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

//Build the Element
export default function Story({ product, ...props }){

	const locale = useLocale();
	const t      = useTranslations();

	const refs = {
		section:    useRef(),
		content:    useRef(),
		background: useRef(),
		title:      useRef(),
		subhead:    useRef(),
		article:    useRef(),
		scroller:   useRef(),
		buttons:    useRef()
	};

	//On Mount
	useGSAP(() => {

		//Skip if it hasn't loaded
		if( !refs.section.current ) return;

		//Prepare the MatchMedia
		const mm = gsap.matchMedia();

		//Set Variables
		const $section      = refs.section.current;
		const $content      = refs.content.current;
		const $background   = refs.background.current;
		const $title        = refs.title.current;
		const $subhead      = refs.subhead.current;
		const $article      = refs.article.current;
		const $buttons      = refs.buttons.current;
		const $header       = header.current;
		const Story         = {
			start: () => {
			},
			end: () => {
				$header.classList.remove('locked');
				$section.classList.remove('active');
			}
		};

		//
		mm.add('(min-width: 1100px)', context => {

			//Create Timeline
			const timeline = gsap.timeline({
				scrollTrigger: {
					trigger:                $content,
					invalidateOnRefresh:    true,
					scrub:                  true,
					pin:                    true,
					pinSpacing:             true,
					markers:                false,
					start:                  '0 0',
					end:                    () => {
						return `+=${
							$content.offsetHeight * 3 }`;
					},
					onEnter: () => {
						$header.classList.add('locked');
						$section.classList.add('active');
					},
					onLeave: () => $header.classList.remove('locked'),
					onEnterBack: () => $header.classList.add('locked'),
					onLeaveBack: () => {
						$header.classList.remove('locked');
						$section.classList.remove('active');
					}
				}
			});

			//Start
			timeline.fromTo( $background ,{ opacity:0.25 },{ opacity:1, duration: 2 });

			//Title Effect
			$title.querySelectorAll(':scope > *').forEach( $span => {
				timeline.fromTo( $span , { opacity:0, yPercent:5 },{ opacity:1, yPercent:0, duration: 4 });
				timeline.fromTo( $span , {}, { duration: 3 });
			});

			//Add Delay
			timeline.fromTo( $title , {} , { duration: 5 });

			//Subheader Effect
			timeline.fromTo( $subhead , { opacity:0, yPercent:25 },{ opacity:1 , yPercent:0, duration: 6 } );

			//Add Delay
			timeline.fromTo( $title , {} , { duration: 5 });

			//
			$article.querySelectorAll(':scope > *').forEach( $p => {
				timeline.fromTo( $p , { opacity:0, yPercent:15 },{ opacity:1, yPercent:0, duration: 10 });
				timeline.fromTo( $p , {} , { duration: 2 });
			});

			//Add Delay
			timeline.fromTo( $title , {} , { duration: 5 });

			//Start
			timeline.fromTo( [ $background , $title , $subhead , $article ] ,{ opacity:1 },{ opacity:0.25, duration: 4 });

		});
/*
		//
		mm.add('(max-width: 1099px)', context => {

			//Create Timeline
			let timeline = gsap.timeline({
				scrollTrigger: {
					trigger:                $content,
					invalidateOnRefresh:    true,
					scrub:                  true,
					pin:                    false,
					markers:                false,
					start:                  '-50% left',
					end:                    '0 left',
				},
			});

			//Title Effect
			$title.querySelectorAll(':scope > *').forEach( $span => {
				timeline.fromTo( $span , { opacity:0, yPercent:5 },{ opacity:1, yPercent:0 });
			});

			//Subheader Effect
			timeline.fromTo( $subhead , { opacity:0, yPercent:25 },{ opacity:1 , yPercent:0 } );

			//
			$article.querySelectorAll(':scope > *').forEach( $p => {
				timeline.fromTo( $p , { opacity:0, yPercent:15 },{ opacity:1, yPercent:0 });
			});

			timeline.fromTo( $buttons , { opacity:0, yPercent:15 },{ opacity:1, yPercent:0 });

		});
*/
	}, { scope: refs.section });

	//Return
	return (
		<div id="story" ref={ refs.section }>
			<div className="content" ref={ refs.content }>
				<div className="wrap" style={(()=>{
					return ( product.story.background > 0 ? {
						background: `rgba(0,0,0,${  product.story.background  })`
					} : {} );
				})()}>
					<div className="grid container">
						<div className="col">
							<section id="caption">
								<h2 ref={ refs.title } dangerouslySetInnerHTML={{
									__html: product.story.title?.[ locale ]
								}} />
								<p ref={ refs.subhead } dangerouslySetInnerHTML={{
									__html: product.story.subheader?.[ locale ]
								}} />
							</section>
						</div>
						<div className="col">

						</div>
						<div className="col">
							<section id="article" ref={ refs.article } dangerouslySetInnerHTML={{
								__html: product.story.content?.[ locale ]
							}} />
							<div className="buttons" ref={ refs.buttons }>
								<Button onClick={()=>{
									refs.article.current.classList.add('open');
								}} type="white" inverted>{ t('Pages.Product.Story.button') }</Button>
							</div>
						</div>
					</div>
				</div>
				<div className="bg" ref={ refs.background }>
					{ product.story.media.image && (
						<Image src={ product.story.media.image.src } alt="" fill />
					)}
					{ product.story.media.video && (
						<div className="video">
							<div>
								<iframe src={ (()=>{

									//Vimeo
									if( /^\d+$/.test( product.story.media.video ) )
										return `https://player.vimeo.com/video/${ product.story.media.video }?badge=0&autopause=0&autoplay=true&player_id=0&loop=true`;

									//Youtube
									return `https://www.youtube.com/embed/${ product.story.media.video }?autoplay=1&mute=1&loop=1&color=white&controls=0&modestbranding=1&playsinline=1&rel=0&enablejsapi=1&playlist=${ product.story.media.video }`;end;

								})() } frameBorder="0" allow={ 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture' } allowfullscreen></iframe>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>


	);

}